import type { ReactElement } from 'react';
import { X } from 'lucide-react';
import { Button, Dialog, IconButton, Notice } from '@/components';
import { useInterfaceLanguage } from '@/lib/interface-language';

export interface PasteCardsDialogProps {
  readonly open: boolean;
  readonly listTitle: string;
  readonly text: string;
  readonly lineCount: number;
  readonly busy?: boolean | undefined;
  readonly error?: string | null | undefined;
  readonly onSingleCard: () => void;
  readonly onCardsPerLine: () => void;
  readonly onCancel: () => void;
}

export function PasteCardsDialog({
  open,
  listTitle,
  text,
  lineCount,
  busy = false,
  error,
  onSingleCard,
  onCardsPerLine,
  onCancel,
}: PasteCardsDialogProps): ReactElement | null {
  const { t } = useInterfaceLanguage();

  if (!open) return null;

  return (
    <Dialog
      open
      onOpenChange={(nextOpen) => {
        if (!nextOpen) onCancel();
      }}
      size="sm"
      title={t('copyMove.paste.title')}
      description={t('copyMove.paste.description', { list: listTitle })}
      closeControl={
        <IconButton label={t('copyMove.paste.close')} size="sm" variant="quiet">
          <X aria-hidden="true" className="size-4" />
        </IconButton>
      }
      footer={
        <>
          <Button size="sm" variant="secondary" onClick={onCancel}>
            {t('copyMove.cancel')}
          </Button>
          <Button size="sm" variant="secondary" disabled={busy} onClick={onSingleCard}>
            {t('copyMove.paste.singleCard')}
          </Button>
          <Button
            size="sm"
            variant="primary"
            busy={busy}
            disabled={lineCount === 0}
            disabledReason={lineCount === 0 ? t('copyMove.paste.needsLine') : undefined}
            onClick={onCardsPerLine}
          >
            {/* ⚠ The count is passed TWICE: into `values` for the `{count}`
                slot and as the arm selector. Omit the third argument and
                `Create 1 cards` renders with nothing red. */}
            {t('copyMove.paste.submit', { count: lineCount }, lineCount)}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        {error ? (
          <Notice tone="bad" title={t('copyMove.paste.errorTitle')} body={error} />
        ) : null}
        <p className="text-sm text-ink">{t('copyMove.paste.body')}</p>
        <pre className="max-h-40 overflow-auto rounded-card border border-border bg-surface-raised p-2 text-xs text-ink-muted">
          {text}
        </pre>
      </div>
    </Dialog>
  );
}
